/**
 * ExploratoryBanner - Mandatory guardrail banner
 *
 * Persistent banner that reminds users this is exploratory output.
 * LEGALLY IMPORTANT - must always be visible.
 *
 * Also explains the Local vs Google processing lanes.
 *
 * @module ExploratoryBanner
 */

import React, { useState } from 'react';

import { Zap, Info, X, Cloud, HardDrive, ArrowRight } from 'lucide-react';

const ExploratoryBanner = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="flex-shrink-0 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border-b border-amber-200/50 dark:border-amber-800/30">
      <div className="px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <div className="p-1 bg-amber-200 dark:bg-amber-900/50 rounded-lg">
            <Zap className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">
            ⚡ Exploratory output — not deterministic or legally binding
          </span>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-amber-200/50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
          title="More info"
        >
          <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        </button>
      </div>

      {/* Expanded explanation */}
      {isExpanded && (
        <div className="px-4 pb-3 text-sm text-amber-700 dark:text-amber-300 bg-amber-50/50 dark:bg-amber-950/30 border-t border-amber-200/30 dark:border-amber-800/30">
          <div className="flex items-start gap-3 pt-2">
            <div className="flex-1 space-y-4">
              {/* What this means */}
              <div>
                <p className="font-medium mb-1">What this means:</p>
                <ul className="list-disc list-inside space-y-1 text-amber-600 dark:text-amber-400">
                  <li>
                    Results are for <strong>exploration and drafting</strong> only
                  </li>
                  <li>AI-generated insights may contain inaccuracies</li>
                  <li>Not suitable for court submissions or final decisions</li>
                  <li>Always verify important information from primary sources</li>
                </ul>
              </div>

              {/* Storage & Processing */}
              <div className="border-t border-amber-200/30 dark:border-amber-800/30 pt-3">
                <p className="font-medium mb-2">Storage & Processing:</p>
                <div className="flex items-start gap-2 p-2 bg-amber-100/50 dark:bg-amber-900/20 rounded-lg">
                  <HardDrive className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-700 dark:text-amber-300 text-xs">
                      Processing Lane
                    </p>
                    <p className="text-xs text-amber-600/80 dark:text-amber-400/80">
                      Private, immediate, cost-effective. Files stored on your server.
                    </p>
                  </div>
                </div>
                <p className="text-[10px] text-amber-500 dark:text-amber-500 mt-2 flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" />
                  Your files are <strong>always saved</strong> — nothing is temporary.
                </p>
              </div>

              <p className="text-xs text-amber-500 dark:text-amber-500 border-t border-amber-200/30 dark:border-amber-800/30 pt-2">
                For verified, deterministic analysis, use Intelligence Studio.
              </p>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1 hover:bg-amber-200/50 dark:hover:bg-amber-900/30 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploratoryBanner;
